import React from 'react';
import { Box, Typography, Button, Chip, Divider, Avatar, Paper, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from '../i18n/i18n';
import PsychometricResultsSummary from './PsychometricResultsSummary';

const labelSx = { fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 };
const headerCellSx = { fontWeight: 600, bgcolor: 'grey.50' };

function HireDetailsPanel({ hire, isAdmin, onClose, onOpenMenu, onViewFull, getBranchInfo, getContrastColor }) {
  const { t } = useTranslation();
  const modules = Object.entries(hire.learning_modules || {});

  return (
    <Paper
      elevation={0}
      sx={{ position: 'relative', border: 2, borderColor: 'primary.main', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2, mt: 0, zIndex: 10, minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}
    >
      <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8 }} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      {isAdmin && (
        <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 44 }} onClick={onOpenMenu}>
          <MoreVertIcon />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'white', fontSize: '1rem', fontWeight: 600 }}>
          {(hire.fullname || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{hire.fullname}</Typography>
          <Typography variant="caption" color="text.secondary">{hire.email}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>{t('position')}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{hire.position}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>{t('status')}</Typography>
          <Chip
            label={hire.status}
            size="small"
            color={hire.status === 'active' ? 'success' : hire.status === 'pending' ? 'warning' : 'default'}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={labelSx}>{t('branches') || 'Branches'}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(hire.branches || []).map((bid) => {
            const br = getBranchInfo(bid) || {};
            const color = (br.config && br.config.color) || br.color || '#e0e0e0';
            return <Chip key={bid} label={br.name || bid} size="small" sx={{ backgroundColor: color, color: getContrastColor(color), height: 24, fontSize: '0.75rem' }} />;
          })}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <PsychometricResultsSummary results={hire.psychometric_test_results} />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t('learningmodules_progress')}</Typography>
        {modules.length > 0 ? (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>{t('module')}</TableCell>
                  <TableCell sx={headerCellSx} align="right">{t('total_score')}</TableCell>
                  <TableCell sx={headerCellSx} align="right">{t('results')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map(([moduleId, progress]) => (
                  <TableRow key={moduleId} sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{progress.title || moduleId}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {progress.state === 'taken' ? (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{progress.score}%</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={progress.state === 'assigned' ? t('assigned') : progress.passed ? t('passed') : t('failed')}
                        size="small"
                        color={progress.state === 'assigned' ? 'default' : progress.passed ? 'success' : 'error'}
                        sx={{ fontWeight: 500, height: 24 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('learningmodules_no_modules') || 'No learning modules assigned.'}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Button variant="outlined" fullWidth onClick={onViewFull} sx={{ textTransform: 'none' }}>
        {t('view_full_info')}
      </Button>
    </Paper>
  );
}

export default HireDetailsPanel;
